import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, Search, Filter, Plus, Mail, User, Package, Calendar, 
  Clock, CheckCircle, XCircle, ChevronRight, ChevronDown, RefreshCw,
  BarChart3, Award, Send, Trash2, Edit, X, Settings, Star,
  ChevronLeft, ArrowUpDown, TrendingUp, CalendarDays
} from 'lucide-react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const RECLAMATION_TYPES = {
  colis_non_recu: { label: "Colis non reçu", color: "bg-red-100 text-red-700" },
  colis_endommage: { label: "Colis endommagé", color: "bg-orange-100 text-orange-700" },
  mauvaise_adresse: { label: "Mauvaise adresse", color: "bg-yellow-100 text-yellow-700" },
  sans_signature: { label: "Colis laissé sans signature", color: "bg-blue-100 text-blue-700" },
  retard: { label: "Retard de livraison", color: "bg-purple-100 text-purple-700" },
  autre: { label: "Autre", color: "bg-gray-100 text-gray-700" }
};

const STATUS_CONFIG = {
  non_traite: { label: "Non traité", color: "bg-red-100 text-red-700", icon: XCircle },
  en_cours: { label: "En cours", color: "bg-orange-100 text-orange-700", icon: Clock },
  traite_livreur: { label: "Traité par livreur", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  traite: { label: "Traité", color: "bg-green-100 text-green-700", icon: CheckCircle }
};

const AdminReclamations = ({ user }) => {
  const [reclamations, setReclamations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pdas, setPdas] = useState([]);
  const [stats, setStats] = useState({ global_stats: {}, driver_stats: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'stats', 'weekly'
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [dateFilterMode, setDateFilterMode] = useState('all'); // all, today, week, month, year, custom
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  
  // Sorting
  const [sortField, setSortField] = useState('date_reclamation');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Stats period filter with navigation
  const [statsPeriod, setStatsPeriod] = useState('week'); // week, month, all
  const [statsOffset, setStatsOffset] = useState(0); // 0 = current, -1 = previous, etc.
  const [statsPeriodLabel, setStatsPeriodLabel] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    date_reclamation: new Date().toISOString().split('T')[0],
    reference_colis: '',
    livreur_id: '',
    client_destinataire: '',
    client_adresse: '',
    client_code_postal: '',
    client_ville: '',
    type_reclamation: 'colis_non_recu',
    description: '',
    pda_id: ''
  });
  
  // Auto-fetched PDA info
  const [autoFetchedPda, setAutoFetchedPda] = useState(null);

  useEffect(() => {
    fetchData();
    fetchPdas();
    fetchDrivers(); // Load drivers separately as fallback
  }, []);

  const fetchDrivers = async () => {
    try {
      // Fetch Colis Privé drivers from users API
      const res = await axios.get(`${API}/api/users`, { withCredentials: true });
      const allUsers = res.data.users || res.data || [];
      // Filter only employees with Colis Privé activity
      const cpDrivers = allUsers.filter(user => 
        user.role === 'employee' &&
        user.assigned_activity && 
        (user.assigned_activity === 'Colis Privé' || user.assigned_activity.includes('Colis Priv'))
      );
      if (cpDrivers.length > 0) {
        const formattedDrivers = cpDrivers.map(d => ({
          user_id: d.user_id,
          name: `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.name || 'Inconnu',
          agency: d.agency || ''
        }));
        console.log('Drivers from users API:', formattedDrivers);
        setDrivers(prev => prev.length === 0 ? formattedDrivers : prev);
      }
    } catch (error) {
      console.error('Failed to fetch drivers from users:', error);
    }
  };

  const getStatsDateRange = (period, offset = 0) => {
    const now = new Date();
    let from = '', to = '', label = '';
    
    if (period === 'week') {
      // Calculate Monday of the target week
      const d = new Date(now);
      const dayOfWeek = d.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so go back 6 days
      d.setDate(d.getDate() - daysToMonday); // Now d is Monday of current week
      d.setDate(d.getDate() + (offset * 7)); // Apply offset
      
      const monday = new Date(d);
      const sunday = new Date(d);
      sunday.setDate(sunday.getDate() + 6);
      
      from = monday.toISOString().split('T')[0];
      to = sunday.toISOString().split('T')[0];
      
      // Format label: "Semaine du 11 au 17 mars 2026 (S12)"
      const weekNum = getWeekNumber(monday);
      const monthFrom = monday.toLocaleDateString('fr-FR', { month: 'long' });
      const monthTo = sunday.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      if (monthFrom === monthTo.split(' ')[0]) {
        label = `Semaine du ${monday.getDate()} au ${sunday.getDate()} ${monthTo} (S${weekNum})`;
      } else {
        label = `Semaine du ${monday.getDate()} ${monthFrom} au ${sunday.getDate()} ${monthTo} (S${weekNum})`;
      }
    } else if (period === 'month') {
      // Calculate target month with offset
      const targetDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const lastDay = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
      
      from = targetDate.toISOString().split('T')[0];
      to = lastDay.toISOString().split('T')[0];
      
      label = targetDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      label = label.charAt(0).toUpperCase() + label.slice(1); // Capitalize
    } else {
      // 'all' - no filter
      label = 'Toutes les dates';
    }
    return { from, to, label };
  };

  // Helper to get week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const fetchStats = async (period, offset = 0) => {
    try {
      const { from, to, label } = getStatsDateRange(period, offset);
      setStatsPeriodLabel(label);
      
      const params = { filter_by: 'transmission' }; // Toujours filtrer par date de transmission
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      
      const res = await axios.get(`${API}/api/reclamations/stats`, { params, withCredentials: true });
      setStats(res.data);
      const driversData = res.data.driver_stats || [];
      if (driversData.length > 0) {
        setDrivers(prev => {
          // Merge stats drivers with existing user drivers
          const existingIds = new Set(prev.map(d => d.user_id));
          const newDrivers = driversData
            .filter(d => !existingIds.has(d.livreur_id))
            .map(d => ({ user_id: d.livreur_id, name: d.livreur_name, agency: d.livreur_agency }));
          return [...prev, ...newDrivers];
        });
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reclamationsRes] = await Promise.all([
        axios.get(`${API}/api/reclamations`, { withCredentials: true }),
      ]);
      setReclamations(reclamationsRes.data.reclamations || []);
      await fetchStats(statsPeriod, statsOffset);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPdas = async () => {
    try {
      const res = await axios.get(`${API}/api/pdas`, { withCredentials: true });
      setPdas(res.data.pdas || []);
    } catch (error) {
      console.error('Failed to fetch PDAs:', error);
    }
  };

  // Refetch stats when period or offset changes
  useEffect(() => {
    if (!loading) fetchStats(statsPeriod, statsOffset);
  }, [statsPeriod, statsOffset]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Auto-fetch PDA when driver and date are selected
  const fetchDriverPda = async (driverId, date) => {
    if (!driverId || !date) {
      setAutoFetchedPda(null);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/api/reclamations/driver-pda/${driverId}/${date}`, { withCredentials: true });
      if (res.data.found) {
        setAutoFetchedPda(res.data);
        // Auto-fill the PDA if found
        if (res.data.pda_id && !formData.pda_id) {
          setFormData(prev => ({ ...prev, pda_id: res.data.pda_id }));
        }
      } else {
        setAutoFetchedPda(null);
      }
    } catch (error) {
      console.error('Failed to fetch driver PDA:', error);
      setAutoFetchedPda(null);
    }
  };

  // Effect to fetch PDA when driver or date changes
  useEffect(() => {
    if (formData.livreur_id && formData.date_reclamation) {
      fetchDriverPda(formData.livreur_id, formData.date_reclamation);
    }
  }, [formData.livreur_id, formData.date_reclamation]);

  const handleUpdateNotes = async (reclamationId, notePda, noteReclamation) => {
    try {
      await axios.put(`${API}/api/reclamations/${reclamationId}/notes`, {
        note_pda: notePda,
        note_reclamation: noteReclamation
      }, { withCredentials: true });
      fetchData();
    } catch (error) {
      alert('Erreur lors de la mise à jour des notes');
    }
  };

  // Filter reclamations
  const filteredReclamations = useMemo(() => {
    let filtered = [...reclamations];
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.reference_colis?.toLowerCase().includes(search) ||
        r.livreur_name?.toLowerCase().includes(search) ||
        r.client_destinataire?.toLowerCase().includes(search)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    // Driver filter
    if (driverFilter !== 'all') {
      filtered = filtered.filter(r => r.livreur_id === driverFilter);
    }
    
    // Date filter
    if (dateFilterMode === 'today') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(r => r.date_reclamation === today);
    } else if (dateFilterMode === 'week') {
      const now = new Date();
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay() + 1);
      const weekStart = d.toISOString().split('T')[0];
      filtered = filtered.filter(r => r.date_reclamation >= weekStart);
    } else if (dateFilterMode === 'month') {
      if (selectedMonth) {
        filtered = filtered.filter(r => r.date_reclamation?.startsWith(selectedMonth));
      } else {
        const now = new Date();
        const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        filtered = filtered.filter(r => r.date_reclamation?.startsWith(monthPrefix));
      }
    } else if (dateFilterMode === 'year') {
      filtered = filtered.filter(r => r.date_reclamation?.startsWith(selectedYear));
    } else if (dateFilterMode === 'custom') {
      if (dateFrom) filtered = filtered.filter(r => r.date_reclamation >= dateFrom);
      if (dateTo) filtered = filtered.filter(r => r.date_reclamation <= dateTo);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'date_reclamation':
          valA = a.date_reclamation || '';
          valB = b.date_reclamation || '';
          break;
        case 'date_traitement':
          valA = a.date_traitement || '';
          valB = b.date_traitement || '';
          break;
        case 'livreur_name':
          valA = a.livreur_name || '';
          valB = b.livreur_name || '';
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        case 'type_reclamation':
          valA = a.type_reclamation || '';
          valB = b.type_reclamation || '';
          break;
        default:
          valA = a[sortField] || '';
          valB = b[sortField] || '';
      }
      if (sortDirection === 'asc') {
        return valA < valB ? -1 : valA > valB ? 1 : 0;
      } else {
        return valA > valB ? -1 : valA < valB ? 1 : 0;
      }
    });
    
    return filtered;
  }, [reclamations, searchTerm, statusFilter, driverFilter, dateFilterMode, dateFrom, dateTo, selectedMonth, selectedYear, sortField, sortDirection]);

  // Handler: click on driver name in stats → switch to list + filter
  const handleDriverClick = (livreurId) => {
    console.log('Clic sur livreur:', livreurId);
    // Switch to list tab first
    setActiveTab('list');
    // Set driver filter - use the livreur_id directly
    setDriverFilter(livreurId);
    // Reset other filters
    setDateFilterMode('all');
    setStatusFilter('all');
    setSearchTerm('');
    // Scroll to top so the user sees the list
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleAddReclamation = async () => {
    // Validation des champs obligatoires
    const missing = [];
    if (!formData.livreur_id) missing.push('Nom du livreur');
    if (!formData.pda_id && !autoFetchedPda) missing.push('PDA');
    if (!formData.client_adresse) missing.push('Adresse du client');
    if (!formData.reference_colis) missing.push('Référence du colis');
    if (!formData.date_reclamation) missing.push('Date');
    
    if (missing.length > 0) {
      alert(`Champs obligatoires manquants :\n- ${missing.join('\n- ')}`);
      return;
    }
    
    try {
      await axios.post(`${API}/api/reclamations`, formData, { withCredentials: true });
      setShowAddModal(false);
      setFormData({
        date_reclamation: new Date().toISOString().split('T')[0],
        reference_colis: '',
        livreur_id: '',
        client_destinataire: '',
        client_adresse: '',
        client_code_postal: '',
        client_ville: '',
        type_reclamation: 'colis_non_recu',
        description: '',
        pda_id: ''
      });
      setAutoFetchedPda(null);
      fetchData();
    } catch (error) {
      console.error('Failed to add reclamation:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'ajout de la réclamation');
    }
  };

  const handleUpdateReclamation = async () => {
    if (!selectedReclamation) return;
    
    try {
      await axios.put(`${API}/api/reclamations/${selectedReclamation.reclamation_id}`, {
        status: selectedReclamation.status,
        responsable_traitement: selectedReclamation.responsable_traitement,
        commentaire_interne: selectedReclamation.commentaire_interne,
        date_reclamation: selectedReclamation.date_reclamation,
        reference_colis: selectedReclamation.reference_colis,
        livreur_id: selectedReclamation.livreur_id,
        client_destinataire: selectedReclamation.client_destinataire,
        client_adresse: selectedReclamation.client_adresse,
        client_code_postal: selectedReclamation.client_code_postal,
        client_ville: selectedReclamation.client_ville,
        type_reclamation: selectedReclamation.type_reclamation,
        description: selectedReclamation.description,
        pda_id: selectedReclamation.pda_id
      }, { withCredentials: true });
      
      setShowEditModal(false);
      setSelectedReclamation(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update reclamation:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteReclamation = async (reclamationId) => {
    if (!confirm('Supprimer cette réclamation ?')) return;
    
    try {
      await axios.delete(`${API}/api/reclamations/${reclamationId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSendReminder = async (reclamationId) => {
    setSendingReminder(reclamationId);
    try {
      const response = await axios.post(
        `${API}/api/reclamations/${reclamationId}/send-reminder`,
        {},
        { withCredentials: true }
      );
      alert(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to send reminder:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'envoi du rappel');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleSendBulkReminder = async (livreurId) => {
    setSendingReminder(livreurId);
    try {
      const response = await axios.post(
        `${API}/api/reclamations/send-bulk-reminders`,
        { livreur_id: livreurId },
        { withCredentials: true }
      );
      alert(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to send bulk reminder:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingReminder(null);
    }
  };

  const openEditModal = (reclamation) => {
    setSelectedReclamation({ ...reclamation });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <Layout user={user} activeModule="b2b">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule="b2b">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-red-500" />
              Réclamations Colis Privé
            </h1>
            <p className="text-slate-500 mt-1">Suivi des réclamations clients par livreur</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une réclamation
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.global_stats?.total_reclamations || 0}</p>
                </div>
                <Package className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Non traités</p>
                  <p className="text-2xl font-bold text-red-900">{stats.global_stats?.non_traite || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">En cours</p>
                  <p className="text-2xl font-bold text-orange-900">{stats.global_stats?.en_cours || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Traités</p>
                  <p className="text-2xl font-bold text-green-900">{stats.global_stats?.traite || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-gradient-to-br ${stats.global_stats?.overdue_count > 0 ? 'from-red-100 to-red-50 border-red-400' : 'from-yellow-50 to-white border-yellow-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${stats.global_stats?.overdue_count > 0 ? 'text-red-700' : 'text-yellow-600'}`}>
                    En retard (+48h)
                  </p>
                  <p className={`text-2xl font-bold ${stats.global_stats?.overdue_count > 0 ? 'text-red-900' : 'text-yellow-900'}`}>
                    {stats.global_stats?.overdue_count || 0}
                  </p>
                </div>
                <AlertTriangle className={`w-8 h-8 ${stats.global_stats?.overdue_count > 0 ? 'text-red-600 animate-pulse' : 'text-yellow-500'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs - Simplifié: plus de tab stats ici, il est dans la page Statistiques Livreurs */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'list' 
                ? 'text-red-600 border-b-2 border-red-500' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Liste des réclamations
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'list' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher (réf. colis, livreur, client)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
              >
                <option value="all">Tous les statuts</option>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
              <select
                value={driverFilter}
                onChange={(e) => setDriverFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                data-testid="filter-driver"
              >
                <option value="all">Tous les livreurs</option>
                {drivers.map(d => (
                  <option key={d.user_id || d.livreur_id} value={d.user_id || d.livreur_id}>
                    {d.name || d.livreur_name}
                  </option>
                ))}
              </select>
              <select
                value={dateFilterMode}
                onChange={(e) => { setDateFilterMode(e.target.value); }}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                data-testid="filter-date-mode"
              >
                <option value="all">Toutes les dates</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="month">Par mois</option>
                <option value="year">Par année</option>
                <option value="custom">Période personnalisée</option>
              </select>
            </div>
            
            {/* Extended date filters */}
            {dateFilterMode === 'month' && (
              <div className="flex gap-2 items-center">
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                  data-testid="filter-month"
                />
                {selectedMonth && (
                  <button onClick={() => setSelectedMonth('')} className="text-xs text-slate-500 hover:text-red-500">Effacer</button>
                )}
              </div>
            )}
            {dateFilterMode === 'year' && (
              <div className="flex gap-2 items-center">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                  data-testid="filter-year"
                >
                  {[2024, 2025, 2026, 2027].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}
            {dateFilterMode === 'custom' && (
              <div className="flex gap-2 items-center flex-wrap">
                <label className="text-sm text-slate-600">Du</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                  data-testid="filter-date-from"
                />
                <label className="text-sm text-slate-600">Au</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                  data-testid="filter-date-to"
                />
                {(dateFrom || dateTo) && (
                  <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-slate-500 hover:text-red-500">Effacer</button>
                )}
              </div>
            )}
            
            {/* Active filter indicator */}
            {(driverFilter !== 'all' || dateFilterMode !== 'all') && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Filtres actifs :</span>
                {driverFilter !== 'all' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
                    Livreur: {drivers.find(d => (d.user_id || d.livreur_id) === driverFilter)?.name || drivers.find(d => (d.user_id || d.livreur_id) === driverFilter)?.livreur_name || driverFilter}
                    <button onClick={() => setDriverFilter('all')} className="ml-1 hover:text-red-500">&times;</button>
                  </span>
                )}
                {dateFilterMode !== 'all' && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium flex items-center gap-1">
                    {dateFilterMode === 'today' ? "Aujourd'hui" : dateFilterMode === 'week' ? 'Cette semaine' : dateFilterMode === 'month' ? `Mois: ${selectedMonth || 'actuel'}` : dateFilterMode === 'year' ? `Année: ${selectedYear}` : `${dateFrom || '...'} → ${dateTo || '...'}`}
                    <button onClick={() => setDateFilterMode('all')} className="ml-1 hover:text-red-500">&times;</button>
                  </span>
                )}
                <span className="text-slate-400">({filteredReclamations.length} résultats)</span>
              </div>
            )}

            {/* Reclamations List */}
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg border">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th 
                      className="text-left p-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('date_reclamation')}
                    >
                      <div className="flex items-center gap-1">
                        Date création
                        <ArrowUpDown className={`w-3 h-3 ${sortField === 'date_reclamation' ? 'text-red-500' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">Réf. Colis</th>
                    <th 
                      className="text-left p-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('livreur_name')}
                    >
                      <div className="flex items-center gap-1">
                        Livreur
                        <ArrowUpDown className={`w-3 h-3 ${sortField === 'livreur_name' ? 'text-red-500' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-600">Client</th>
                    <th 
                      className="text-left p-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('type_reclamation')}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        <ArrowUpDown className={`w-3 h-3 ${sortField === 'type_reclamation' ? 'text-red-500' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th className="text-center p-3 text-xs font-semibold text-slate-600">Notes</th>
                    <th 
                      className="text-center p-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Statut
                        <ArrowUpDown className={`w-3 h-3 ${sortField === 'status' ? 'text-red-500' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th 
                      className="text-center p-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('date_traitement')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Traitement
                        <ArrowUpDown className={`w-3 h-3 ${sortField === 'date_traitement' ? 'text-red-500' : 'text-slate-400'}`} />
                      </div>
                    </th>
                    <th className="text-center p-3 text-xs font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredReclamations.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-slate-500">
                        Aucune réclamation trouvée
                      </td>
                    </tr>
                  ) : (
                    filteredReclamations.map((rec) => {
                      const statusConfig = STATUS_CONFIG[rec.status] || STATUS_CONFIG.non_traite;
                      const typeConfig = RECLAMATION_TYPES[rec.type_reclamation] || RECLAMATION_TYPES.autre;
                      const StatusIcon = statusConfig.icon;
                      const pda = pdas.find(p => p.pda_id === rec.pda_id);
                      
                      return (
                        <tr 
                          key={rec.reclamation_id} 
                          className={`hover:bg-slate-50 ${rec.is_overdue ? 'bg-red-50' : ''}`}
                        >
                          <td className="p-3 text-sm">
                            <div className="flex items-center gap-2">
                              {rec.is_overdue && (
                                <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" title="En retard (+48h)" />
                              )}
                              {new Date(rec.date_reclamation).toLocaleDateString('fr-FR')}
                            </div>
                          </td>
                          <td className="p-3 text-sm font-mono font-medium">{rec.reference_colis}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <div>
                                {/* Nom du livreur */}
                                {rec.livreur_name ? (
                                  <span className={`text-sm font-medium ${rec.is_overdue ? 'text-red-700' : 'text-slate-900'}`}>
                                    {rec.livreur_name}
                                  </span>
                                ) : (
                                  <span className="text-sm text-slate-400 italic">Livreur non identifié</span>
                                )}
                                {rec.livreur_agency && (
                                  <span className="text-xs text-slate-400 ml-1">– {rec.livreur_agency}</span>
                                )}
                                {/* Display PDA - prefer pda_numero (stored), fallback to lookup */}
                                {(rec.pda_numero || pda?.numero) && (
                                  <div className="text-xs text-purple-600 font-medium mt-0.5">
                                    📱 PDA: {rec.pda_numero || pda?.numero}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-sm">
                            <div>
                              <span className="text-slate-700">{rec.client_destinataire || '-'}</span>
                              {(rec.client_adresse || rec.client_ville) && (
                                <div className="text-xs text-slate-400 mt-0.5">
                                  {rec.client_adresse && <span>{rec.client_adresse}</span>}
                                  {rec.client_code_postal && <span> {rec.client_code_postal}</span>}
                                  {rec.client_ville && <span> {rec.client_ville}</span>}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>
                              {typeConfig.label}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex items-center gap-1" title="Note PDA">
                                <span className="text-xs text-slate-500">PDA:</span>
                                <div className="flex">
                                  {[1,2,3,4,5].map(i => (
                                    <Star 
                                      key={i} 
                                      className={`w-3 h-3 cursor-pointer ${i <= (rec.note_pda || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                      onClick={() => handleUpdateNotes(rec.reclamation_id, i, rec.note_reclamation)}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-center gap-1" title="Note réclamation">
                                <span className="text-xs text-slate-500">Récl:</span>
                                <div className="flex">
                                  {[1,2,3,4,5].map(i => (
                                    <Star 
                                      key={i} 
                                      className={`w-3 h-3 cursor-pointer ${i <= (rec.note_reclamation || 0) ? 'text-blue-400 fill-blue-400' : 'text-gray-300'}`}
                                      onClick={() => handleUpdateNotes(rec.reclamation_id, rec.note_pda, i)}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="p-3 text-center text-xs text-slate-500">
                            {rec.date_traitement ? (
                              <span className="text-green-600 font-medium">
                                {new Date(rec.date_traitement).toLocaleDateString('fr-FR')}
                              </span>
                            ) : rec.status === 'non_traite' ? (
                              <span className="text-red-400">En attente</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditModal(rec)}
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {rec.status === 'non_traite' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSendReminder(rec.reclamation_id)}
                                  disabled={sendingReminder === rec.reclamation_id}
                                  title="Envoyer rappel"
                                  className="text-orange-600 hover:text-orange-700"
                                >
                                  {sendingReminder === rec.reclamation_id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600" />
                                  ) : (
                                    <Mail className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteReclamation(rec.reclamation_id)}
                                title="Supprimer"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Reclamation Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter une réclamation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Date de réclamation *</label>
                <Input
                  type="date"
                  value={formData.date_reclamation}
                  onChange={(e) => setFormData({ ...formData, date_reclamation: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Référence colis *</label>
                <Input
                  placeholder="Ex: CP123456789"
                  value={formData.reference_colis}
                  onChange={(e) => setFormData({ ...formData, reference_colis: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Livreur *</label>
              <select
                value={formData.livreur_id}
                onChange={(e) => setFormData({ ...formData, livreur_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg bg-white"
              >
                <option value="">Sélectionner un livreur</option>
                {drivers.map(d => (
                  <option key={d.user_id} value={d.user_id}>
                    {d.name}{d.agency ? ` – ${d.agency}` : ''}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Auto-fetched PDA info */}
            {autoFetchedPda && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-700 mb-1">📱 Mission du livreur détectée</p>
                <div className="text-xs text-blue-600 space-y-1">
                  {autoFetchedPda.pda_numero && <p>PDA utilisé: <strong>{autoFetchedPda.pda_numero}</strong></p>}
                  {autoFetchedPda.vehicle_plate && <p>Véhicule: <strong>{autoFetchedPda.vehicle_plate}</strong></p>}
                  {autoFetchedPda.site_name && <p>Site: <strong>{autoFetchedPda.site_name}</strong></p>}
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700">PDA utilisé</label>
              <select
                value={formData.pda_id}
                onChange={(e) => setFormData({ ...formData, pda_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg bg-white"
              >
                <option value="">Sélectionner un PDA (optionnel)</option>
                {pdas.filter(p => p.active).map(p => (
                  <option key={p.pda_id} value={p.pda_id}>{p.numero}</option>
                ))}
              </select>
              {autoFetchedPda?.pda_numero && !formData.pda_id && (
                <p className="text-xs text-slate-500 mt-1">Le PDA sera automatiquement récupéré depuis la mission</p>
              )}
            </div>
            
            {/* Client info */}
            <div>
              <label className="text-sm font-medium text-slate-700">Client / Destinataire</label>
              <Input
                placeholder="Nom du client (optionnel)"
                value={formData.client_destinataire}
                onChange={(e) => setFormData({ ...formData, client_destinataire: e.target.value })}
              />
            </div>
            
            {/* Adresse client */}
            <div>
              <label className="text-sm font-medium text-slate-700">Adresse du client</label>
              <Input
                placeholder="Ex: 14 rue de la Paix"
                value={formData.client_adresse}
                onChange={(e) => setFormData({ ...formData, client_adresse: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Code postal</label>
                <Input
                  placeholder="Ex: 75002"
                  value={formData.client_code_postal}
                  onChange={(e) => setFormData({ ...formData, client_code_postal: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Ville</label>
                <Input
                  placeholder="Ex: Paris"
                  value={formData.client_ville}
                  onChange={(e) => setFormData({ ...formData, client_ville: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Type de réclamation *</label>
              <select
                value={formData.type_reclamation}
                onChange={(e) => setFormData({ ...formData, type_reclamation: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg bg-white"
              >
                {Object.entries(RECLAMATION_TYPES).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description *</label>
              <textarea
                placeholder="Description de la réclamation..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg resize-none"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Annuler</Button>
            <Button 
              onClick={handleAddReclamation}
              disabled={!formData.reference_colis || !formData.livreur_id || !formData.description}
              className="bg-red-600 hover:bg-red-700"
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Reclamation Modal - FULL EDIT */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la réclamation</DialogTitle>
          </DialogHeader>
          {selectedReclamation && (
            <div className="space-y-4 py-2">
              {/* Row 1: Date + Référence */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Date *</label>
                  <Input
                    type="date"
                    value={selectedReclamation.date_reclamation || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, date_reclamation: e.target.value })}
                    data-testid="edit-date"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Référence colis *</label>
                  <Input
                    value={selectedReclamation.reference_colis || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, reference_colis: e.target.value })}
                    data-testid="edit-reference"
                  />
                </div>
              </div>

              {/* Row 2: Livreur + PDA */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Livreur *</label>
                  <select
                    value={selectedReclamation.livreur_id || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, livreur_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                    data-testid="edit-livreur"
                  >
                    <option value="">-- Sélectionner --</option>
                    {drivers.map(d => (
                      <option key={d.user_id} value={d.user_id}>
                        {d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Inconnu'} {d.agency ? `(${d.agency})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">PDA *</label>
                  <select
                    value={selectedReclamation.pda_id || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, pda_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                    data-testid="edit-pda"
                  >
                    <option value="">-- Sélectionner --</option>
                    {pdas.map(p => (
                      <option key={p.pda_id} value={p.pda_id}>{p.numero} - {p.marque || ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Client + Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Client / Destinataire</label>
                  <Input
                    value={selectedReclamation.client_destinataire || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, client_destinataire: e.target.value })}
                    data-testid="edit-client"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Type de réclamation *</label>
                  <select
                    value={selectedReclamation.type_reclamation || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, type_reclamation: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                    data-testid="edit-type"
                  >
                    {Object.entries(RECLAMATION_TYPES).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 4: Adresse client */}
              <div>
                <label className="text-sm font-medium text-slate-700">Adresse du client *</label>
                <Input
                  value={selectedReclamation.client_adresse || ''}
                  onChange={(e) => setSelectedReclamation({ ...selectedReclamation, client_adresse: e.target.value })}
                  placeholder="Adresse complète"
                  data-testid="edit-adresse"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Code postal</label>
                  <Input
                    value={selectedReclamation.client_code_postal || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, client_code_postal: e.target.value })}
                    data-testid="edit-cp"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Ville</label>
                  <Input
                    value={selectedReclamation.client_ville || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, client_ville: e.target.value })}
                    data-testid="edit-ville"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-slate-700">Description *</label>
                <textarea
                  value={selectedReclamation.description || ''}
                  onChange={(e) => setSelectedReclamation({ ...selectedReclamation, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg resize-none text-sm"
                  rows={3}
                  data-testid="edit-description"
                />
              </div>

              <hr className="my-2" />

              {/* Statut + Responsable */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Statut *</label>
                  <select
                    value={selectedReclamation.status}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                    data-testid="edit-status"
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Responsable</label>
                  <Input
                    placeholder="Nom du responsable"
                    value={selectedReclamation.responsable_traitement || ''}
                    onChange={(e) => setSelectedReclamation({ ...selectedReclamation, responsable_traitement: e.target.value })}
                    data-testid="edit-responsable"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Commentaire interne</label>
                <textarea
                  placeholder="Notes internes..."
                  value={selectedReclamation.commentaire_interne || ''}
                  onChange={(e) => setSelectedReclamation({ ...selectedReclamation, commentaire_interne: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg resize-none text-sm"
                  rows={2}
                  data-testid="edit-commentaire"
                />
              </div>
              
              {selectedReclamation.rappels?.length > 0 && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800">Rappels envoyés:</p>
                  <ul className="text-xs text-yellow-700 mt-1">
                    {selectedReclamation.rappels.map((date, i) => (
                      <li key={i}>• {new Date(date).toLocaleString('fr-FR')}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
            <Button onClick={handleUpdateReclamation} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-edit">
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminReclamations;
